/* app/onboarding/OnboardingOverlay.jsx — рендер обязательного игрового онбординга.
   Монтируется один раз в AppShell. Два режима шага:
   - panel    — центрированная карточка (welcome / celebrate / business / finish);
   - spotlight— затемнение с «дыркой» на реальном элементе + подсказка-пузырь.
   Подсветка следует за элементом КАЖДЫЙ кадр без CSS-перехода (нет отставания при
   скролле); входная анимация проигрывается один раз на шаг (remount по key=step.id).
   Переходы по разделам — по реальному клику пользователя (advance:'goto' + clickThrough:
   затемнение не блокирует клик по подсвеченному пункту меню). Пропустить онбординг
   можно на любом шаге (глобальная кнопка «Пропустить онбординг» → complete); ИИ-шаг
   не может запереть: при явном сбое LLM (через emergencyAfterMs) — мягкий «Продолжить».
   Доступность: фокус уводится в активный шаг, панели aria-modal, есть aria-live анонс,
   reduced-motion гасит smooth-scroll. Телеметрия воронки — trackOnboarding (best-effort). */

import React from 'react';
import ReactDOM from 'react-dom';
import { useAuth } from '../providers/AuthProvider.jsx';
import { useOnboarding, markDoneLocal } from './OnboardingProvider.jsx';
import { DEMO_TTK_PROMPT } from './steps.jsx';
import { fireConfetti } from './confetti.js';
import { Button } from '../../components/ui/Button.jsx';
import { Icon } from '../../components/ui/Icon.jsx';
import { getOverlayRoot } from '../../lib/overlay.jsx';
import * as authApi from '../../lib/api/auth.js';
import { RR } from '../../lib/router.jsx';

  const { useState, useEffect, useLayoutEffect, useRef, useCallback } = React;
  const { useNavigate, useLocation } = RR;

  function prefersReducedMotion() {
    return typeof window !== 'undefined' && window.matchMedia
      && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  // Подстановка реальных значений в копию шага: {dish} → имя созданной нейросетью ТТК.
  function interp(text, vars) {
    if (!text || !vars) return text;
    return text.replace(/\{(\w+)\}/g, (m, k) => (vars[k] != null && vars[k] !== '' ? vars[k] : m));
  }

  /* ── геометрия подсказки относительно «дырки» ── */
  function placeTip(hole, placement, tw, th, vw, vh) {
    const GAP = 16, EDGE = 14;
    if (!hole) return { left: (vw - tw) / 2, top: (vh - th) / 2 };
    let pl = placement || 'auto';
    if (pl === 'auto') {
      const below = vh - (hole.y + hole.h), above = hole.y;
      const right = vw - (hole.x + hole.w), left = hole.x;
      const m = Math.max(below, above, right, left);
      pl = m === below ? 'bottom' : m === above ? 'top' : m === right ? 'right' : 'left';
    }
    let left, top;
    if (pl === 'top') { top = hole.y - th - GAP; left = hole.x + hole.w / 2 - tw / 2; }
    else if (pl === 'bottom') { top = hole.y + hole.h + GAP; left = hole.x + hole.w / 2 - tw / 2; }
    else if (pl === 'left') { left = hole.x - tw - GAP; top = hole.y + hole.h / 2 - th / 2; }
    else { left = hole.x + hole.w + GAP; top = hole.y + hole.h / 2 - th / 2; }
    left = Math.min(Math.max(EDGE, left), vw - tw - EDGE);
    top = Math.min(Math.max(EDGE, top), vh - th - EDGE);
    return { left, top };
  }

  function ProgressBar({ index, total }) {
    const pct = total > 1 ? Math.round((index / (total - 1)) * 100) : 0;
    return (
      <div className="onb-progress" aria-hidden="true">
        <span className="onb-progress__fill" style={{ width: pct + '%' }} />
      </div>
    );
  }

  /* ── подсветка реального элемента ── */
  function SpotlightLayer({ step, index, total, vars, onNext, onResend, onTimeout, onEmergency, onSkip }) {
    const [geo, setGeo] = useState(null);
    const [resendVisible, setResendVisible] = useState(false);
    const [emergencyVisible, setEmergencyVisible] = useState(false);
    const [elapsed, setElapsed] = useState(0);
    const tipRef = useRef(null);
    const lastKey = useRef('');

    // Селекторы могут нести {dish} (имя созданной ТТК) — подставляем реальное значение,
    // чтобы подсветить именно нужную карточку блюда, а не первую попавшуюся .tile--recipe.
    const tSel = interp(step.target, vars);
    const fSel = interp(step.fallbackTarget, vars);

    const measure = useCallback(() => {
      const pad = step.pad != null ? step.pad : 8;
      const vw = window.innerWidth, vh = window.innerHeight;
      const q = (sel) => { if (!sel) return null; try { return document.querySelector(sel); } catch { return null; } };
      const rectOf = (el) => { if (!el) return null; const r = el.getBoundingClientRect(); return (r.width > 0 && r.height > 0) ? r : null; };
      let r = rectOf(q(tSel));
      let fallback = false;
      if (!r && fSel) { r = rectOf(q(fSel)); if (r) fallback = true; }
      let g;
      if (r) {
        const x = Math.max(0, r.left - pad), y = Math.max(0, r.top - pad);
        const w = Math.min(vw - x, r.width + pad * 2), h = Math.min(vh - y, r.height + pad * 2);
        g = { hole: { x, y, w, h }, vw, vh, fallback };
      } else {
        g = { hole: null, vw, vh, fallback: false };
      }
      const tip = tipRef.current;
      g.tip = placeTip(g.hole, step.placement, tip ? tip.offsetWidth : 340, tip ? tip.offsetHeight : 170, vw, vh);
      const key = JSON.stringify(g);
      if (key !== lastKey.current) { lastKey.current = key; setGeo(g); }
    }, [step.id, tSel, fSel]);

    useLayoutEffect(() => { measure(); }, [measure]);

    // слежение за элементом + подвод в зону видимости (reduced-motion → без анимации)
    useEffect(() => {
      let raf = 0;
      const loop = () => { measure(); raf = requestAnimationFrame(loop); };
      raf = requestAnimationFrame(loop);
      let el = null;
      try { el = document.querySelector(tSel) || (fSel && document.querySelector(fSel)); } catch { el = null; }
      if (el && el.scrollIntoView) {
        try { el.scrollIntoView({ block: 'center', behavior: prefersReducedMotion() ? 'auto' : 'smooth' }); } catch { el.scrollIntoView(); }
      }
      return () => cancelAnimationFrame(raf);
    }, [measure, step.id]);

    // увести фокус в подсказку (клавиатурный юзер не теряется под затемнением)
    useEffect(() => { if (tipRef.current) { try { tipRef.current.focus({ preventScroll: true }); } catch { tipRef.current.focus(); } } }, [step.id]);

    // ИИ-шаг: счётчик секунд + страховки (resend / аварийный выход)
    useEffect(() => {
      setResendVisible(false); setEmergencyVisible(false); setElapsed(0);
      const timers = [];
      let ticker = null;
      if (step.timed) ticker = setInterval(() => setElapsed((e) => e + 1), 1000);
      if (step.resendAfterMs) timers.push(setTimeout(() => setResendVisible(true), step.resendAfterMs));
      if (step.emergencyAfterMs) timers.push(setTimeout(() => { setEmergencyVisible(true); onTimeout && onTimeout(); }, step.emergencyAfterMs));
      return () => { timers.forEach(clearTimeout); if (ticker) clearInterval(ticker); };
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [step.id]);

    const hole = geo && geo.hole;
    const vw = geo ? geo.vw : 0, vh = geo ? geo.vh : 0;
    const tip = geo && geo.tip ? geo.tip : { left: -9999, top: -9999 };
    const isButton = step.advance && step.advance.type === 'button';
    const baseHint = interp((geo && geo.fallback) ? (step.fallbackHint || step.hint) : step.hint, vars);
    const activeHint = (step.timed && elapsed >= 3) ? `Считаю… ${elapsed} с` : baseHint;
    const dimCls = 'onb-dim' + (step.clickThrough ? ' onb-dim--through' : '');

    return (
      <div className="onb">
        {hole ? (
          <>
            <div className={dimCls} style={{ left: 0, top: 0, width: '100%', height: hole.y }} />
            <div className={dimCls} style={{ left: 0, top: hole.y + hole.h, width: '100%', height: Math.max(0, vh - (hole.y + hole.h)) }} />
            <div className={dimCls} style={{ left: 0, top: hole.y, width: hole.x, height: hole.h }} />
            <div className={dimCls} style={{ left: hole.x + hole.w, top: hole.y, width: Math.max(0, vw - (hole.x + hole.w)), height: hole.h }} />
            <div className="onb-ring" key={step.id} style={{ left: hole.x, top: hole.y, width: hole.w, height: hole.h }} />
          </>
        ) : (
          <div className={dimCls} style={{ inset: 0, width: '100%', height: '100%' }} />
        )}

        <div className="onb-tip" key={'tip-' + step.id} ref={tipRef} tabIndex={-1}
          role="dialog" aria-label={interp(step.title, vars)}
          style={{ left: tip.left, top: tip.top }}>
          <ProgressBar index={index} total={total} />
          <div className="onb-tip__title">{interp(step.title, vars)}</div>
          <div className="onb-tip__body">{interp(step.body, vars)}</div>
          <div className="onb-tip__foot">
            {isButton
              ? <Button variant="primary" size="sm" iconRight="arrow-right" onClick={onNext}>{step.advance.label || 'Дальше'}</Button>
              : <span className="onb-tip__hint"><span className="onb-pulse" />{activeHint || 'Сделайте действие на подсвеченном элементе'}</span>}
            {onSkip && (
              <button type="button" className="onb-skip" onClick={onSkip}>Пропустить онбординг</button>
            )}
          </div>
          {resendVisible && !isButton && (
            <button type="button" className="onb-tip__resend" onClick={onResend}>Долго отвечает? Отправить ещё раз</button>
          )}
          {emergencyVisible && !isButton && (
            <button type="button" className="onb-tip__resend onb-tip__resend--emergency" onClick={onEmergency}>Ассистент перегружен — продолжить и начать работу</button>
          )}
        </div>
      </div>
    );
  }

  /* ── центрированные панели ── */
  function PanelLayer({ step, index, total, onPrimary, committed, onSkip }) {
    const ref = useRef(null);
    // «Пропустить онбординг» — текстовая ссылка под кнопкой панели (как в Модуле 1);
    // на финальном шаге не нужна (кнопка и так завершает тур).
    const skipLink = (onSkip && step.panel !== 'finish')
      ? <button type="button" className="onb-skip" onClick={onSkip}>Пропустить онбординг</button>
      : null;
    // фокус на первичную кнопку панели (Enter работает сразу, клавиатура не теряется)
    useEffect(() => {
      const el = ref.current && ref.current.querySelector('button');
      if (el) { try { el.focus({ preventScroll: true }); } catch { el.focus(); } }
    }, [step.id]);

    // трофейная строка с реальными цифрами созданной ТТК (если есть)
    const trophy = committed && committed.costText
      ? <div className="onb-trophy"><Icon name="check-circle" size={15} /><span>«{committed.title}» — себестоимость <b>{committed.costText}</b>{committed.fcText ? <> · фудкост <b>{committed.fcText}</b></> : null}</span></div>
      : null;

    let content;
    if (step.panel === 'welcome') {
      content = (
        <div className="onb-card onb-card--hero" role="dialog" aria-modal="true" aria-label="Добро пожаловать">
          <ProgressBar index={index} total={total} />
          <div className="onb-card__badge"><Icon name="sparkles" size={22} /></div>
          <h2 className="onb-card__title">Давайте научимся пользоваться Ресториумом</h2>
          <p className="onb-card__lead">Нейросеть соберёт первую техкарту прямо у вас на глазах — вы только нажимаете. Покажем главные разделы и реальную работу ассистента.</p>
          <div className="onb-card__meta"><Icon name="clock" size={14} /> пара минут · всё по шагам</div>
          <div className="onb-card__actions onb-card__actions--center">
            <Button variant="primary" size="lg" iconRight="arrow-right" onClick={onPrimary}>Поехали</Button>
          </div>
          {skipLink}
        </div>
      );
    } else if (step.panel === 'celebrate') {
      content = (
        <div className="onb-card onb-card--celebrate" role="dialog" aria-modal="true" aria-label="Готово">
          <ProgressBar index={index} total={total} />
          <div className="onb-card__badge onb-card__badge--ok"><Icon name="check-circle" size={26} /></div>
          <h2 className="onb-card__title">Первая техкарта готова!</h2>
          {trophy || <p className="onb-card__lead">Нейросеть собрала её за вас: состав, КБЖУ, выход, себестоимость и фудкост.</p>}
          <p className="onb-card__lead">Обычно это от 10 минут, а вы потратили несколько секунд. Покажем, где она живёт и что ещё умеет Ресториум.</p>
          <div className="onb-card__actions onb-card__actions--center">
            <Button variant="primary" size="lg" iconRight="arrow-right" onClick={onPrimary}>Дальше</Button>
          </div>
          {skipLink}
        </div>
      );
    } else if (step.panel === 'business') {
      const feats = [
        { icon: 'user', label: 'Команда', note: 'Приглашайте коллег, раздавайте роли и работайте в одном контуре с общим доступом к файлам' },
        { icon: 'chat', label: 'Общие чаты и шеринг', note: 'Что один участник создал в пространстве — техкарты и документы в Продукции, чаты — сразу видят остальные' },
        { icon: 'book', label: 'База знаний', note: 'Загрузите стандарты, регламенты, меню и другие документы — ассистент будет их учитывать' },
      ];
      content = (
        <div className="onb-card onb-card--business" role="dialog" aria-modal="true" aria-label="Бизнес-пространство">
          <ProgressBar index={index} total={total} />
          <div className="onb-card__badge"><Icon name="building" size={22} /></div>
          <h2 className="onb-card__title">Бизнес-пространство</h2>
          <p className="onb-card__lead">Сейчас вы в личном пространстве — для одного. Бизнес-пространство открывает работу командой:</p>
          <div className="onb-features">
            {feats.map((f) => (
              <div className="onb-feature" key={f.label}>
                <span className="onb-feature__glyph"><Icon name={f.icon} size={18} /></span>
                <span className="onb-feature__text">
                  <span className="onb-feature__label">{f.label}</span>
                  <span className="onb-feature__note">{f.note}</span>
                </span>
              </div>
            ))}
          </div>
          <div className="onb-card__actions onb-card__actions--center">
            <Button variant="primary" size="lg" iconRight="arrow-right" onClick={onPrimary}>Почти всё</Button>
          </div>
          {skipLink}
        </div>
      );
    } else if (step.panel === 'finish') {
      content = (
        <div className="onb-card onb-card--finish" role="dialog" aria-modal="true" aria-label="Готово">
          <ProgressBar index={index} total={total} />
          <div className="onb-card__badge onb-card__badge--ok"><Icon name="check-circle" size={24} /></div>
          <h2 className="onb-card__title">Готово! Вы освоили Ресториум</h2>
          {trophy}
          {/* текст — из шага (steps.jsx): для пространства без «Аналитики» там подменён вариант */}
          <p className="onb-card__lead">{step.lead}</p>
          <div className="onb-card__actions onb-card__actions--center">
            <Button variant="primary" size="lg" iconRight="arrow-right" onClick={onPrimary}>Начать работу</Button>
          </div>
        </div>
      );
    } else {
      content = null;
    }
    return <div className="onb onb--panel" ref={ref}>{content}</div>;
  }

  function OnboardingOverlay() {
    const onb = useOnboarding();
    const { user, refreshUser } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();
    const [committed, setCommitted] = useState(null);
    // Реальные сущности, созданные в туре (ТТК + дети) — уходят на бэк при завершении,
    // чтобы он их удалил (демо не должно оставлять мусор в свежем пространстве).
    const entitiesRef = useRef([]);

    const complete = useCallback(async (reason) => {
      authApi.trackOnboarding({ kind: 'completed', step: (onb.step && onb.step.id) || '', meta: { reason: reason || 'finish' } });
      markDoneLocal();
      onb.stop();
      // Передаём созданные демо-сущности — бэк удалит их и отвяжет онбординг-чат от квоты.
      try { await authApi.completeOnboarding({ entities: entitiesRef.current }); } catch { /* флаг локально уже стоит */ }
      // Демо-CJM: квест = «Модуль 2» — отметить во флоу (best-effort, complete не ломает)
      if (user && user.onboarding_flow) {
        try { authApi.patchOnboardingFlow({ add_completed: 'assistant_module' }).catch(() => {}); } catch { /* noop */ }
      }
      if (refreshUser) { try { await refreshUser(); } catch { /* noop */ } }
    }, [onb, refreshUser, user]);

    // Явный пропуск онбординга ассистента (по приёмке): доступен на любом шаге,
    // завершает тур штатно (complete → чистка демо-сущностей + отметка
    // assistant_module во флоу), пользователь остаётся на текущем экране.
    const onSkipQuest = useCallback(() => {
      complete('skipped');
    }, [complete]);

    const onResend = useCallback(() => {
      authApi.trackOnboarding({ kind: 'resend', step: 'working' });
      try { window.dispatchEvent(new CustomEvent('onb:resend')); } catch { /* noop */ }
    }, []);
    const onTimeout = useCallback(() => { authApi.trackOnboarding({ kind: 'timeout', step: 'working' }); }, []);
    const onEmergency = useCallback(() => {
      authApi.trackOnboarding({ kind: 'emergency_exit', step: 'working' });
      complete('llm_timeout');
      navigate('/chat');
    }, [complete, navigate]);

    const step = onb && onb.step;

    // запоминаем реальные данные созданной ТТК: имя (для {dish} и трофея) + id
    // (для последующего удаления демо-сущностей на бэке).
    useEffect(() => {
      if (!onb.active) return undefined;
      const h = (e) => {
        const d = e && e.detail;
        if (!d) return;
        if (d.title || d.costText) setCommitted((prev) => ({ ...prev, ...d }));
        if (d.id != null && d.kind) {
          const refs = entitiesRef.current;
          if (!refs.some((r) => r.kind === d.kind && r.id === d.id)) refs.push({ kind: d.kind, id: d.id });
        }
      };
      window.addEventListener('onb:committed', h);
      return () => window.removeEventListener('onb:committed', h);
    }, [onb.active]);

    // конфетти на праздничном шаге
    useEffect(() => {
      if (!onb.active || !step) return undefined;
      if (step.panel === 'celebrate') {
        const t = setTimeout(() => fireConfetti({ originY: 0.34 }), 120);
        return () => clearTimeout(t);
      }
      return undefined;
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [onb.active, step && step.id]);

    // переходы по событию / появлению селектора (spotlight)
    useEffect(() => {
      if (!onb.active || !step || step.kind !== 'spotlight' || !step.advance) return undefined;
      const adv = step.advance;
      if (adv.type === 'event') {
        const h = () => onb.next();
        window.addEventListener(adv.name, h);
        return () => window.removeEventListener(adv.name, h);
      }
      if (adv.type === 'appear') {
        let done = false;
        const check = () => { if (!done && document.querySelector(adv.selector)) { done = true; clearInterval(t); onb.next(); } };
        const t = setInterval(check, 200);
        check();
        return () => clearInterval(t);
      }
      return undefined;
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [onb.active, step && step.id]);

    // переход по разделам: пользователь сам перешёл (URL содержит match)
    useEffect(() => {
      if (!onb.active || !step || !step.advance || step.advance.type !== 'goto') return;
      const url = (location.pathname || '') + (location.search || '');
      const present = url.includes(step.advance.match);
      // gone:true — продвигаемся, когда match ИСЧЕЗ из URL (напр. закрыли карточку ?edit=).
      if (step.advance.gone ? !present : present) onb.next();
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [onb.active, step && step.id, location.pathname, location.search]);

    // На шагах сборки ТТК (step.collapseSidebar) сворачиваем ПОСТОЯННЫЙ сайдбар —
    // фокус на чате. Только если он развёрнут (есть .sidebar--permanent, md+);
    // на drawer-раскладках (sm/xs) сайдбара в потоке нет — ничего не трогаем.
    useEffect(() => {
      const shell = document.querySelector('.app-shell');
      if (!shell) return undefined;
      const wantRail = !!(onb.active && step && step.collapseSidebar
        && document.querySelector('.sidebar--permanent'));
      if (wantRail) {
        shell.classList.add('app-shell--onb-rail');
        return () => shell.classList.remove('app-shell--onb-rail');
      }
      shell.classList.remove('app-shell--onb-rail');
      return undefined;
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [onb.active, step && step.id]);

    if (!onb || !onb.active || !step) return null;

    let body;
    if (step.kind === 'spotlight') {
      body = <SpotlightLayer key={step.id} step={step} index={onb.index} total={onb.total}
        vars={{ dish: (committed && committed.title) || 'ваше блюдо' }}
        onNext={onb.next} onResend={onResend} onTimeout={onTimeout} onEmergency={onEmergency}
        onSkip={onSkipQuest} />;
    } else {
      const onPrimary = step.panel === 'welcome'
        ? () => { navigate('/chat?prefill=' + encodeURIComponent(DEMO_TTK_PROMPT)); onb.next(); }
        : step.panel === 'finish'
          ? () => { complete('finish'); navigate('/chat'); }
          : onb.next; // celebrate / business
      body = <PanelLayer step={step} index={onb.index} total={onb.total} onPrimary={onPrimary}
        committed={committed} onSkip={onSkipQuest} />;
    }

    // визуально-скрытый анонс для скринридера (смена шага)
    const announce = `${step.title || ''}. ${(step.kind === 'spotlight' ? (step.hint || step.body) : step.body) || ''}`;

    const root = getOverlayRoot ? getOverlayRoot() : document.body;
    return ReactDOM.createPortal(
      <>
        <div className="onb-sr" aria-live="polite" role="status">{announce}</div>
        {body}
      </>,
      root
    );
  }

export { OnboardingOverlay };
